import React, { useEffect, useState } from 'react';
import { CalendarCheck } from 'lucide-react';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { EmptyState, InlineSpinner, Card } from './ui';

const STATUS_STYLE = {
  present: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  late: 'bg-amber-100 text-amber-700 border-amber-200',
  excused: 'bg-sky-100 text-sky-700 border-sky-200',
  absent: 'bg-rose-100 text-rose-700 border-rose-200',
};

/**
 * Read-only attendance history. Pass studentId for a parent viewing a child,
 * or omit it for a student viewing their own record.
 */
export const AttendanceHistory = ({ studentId = null }) => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    schoolApi.myAttendance(studentId ? { student_id: studentId } : {})
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [studentId]);

  if (loading) return <Card><InlineSpinner /></Card>;
  if (!data || data.total === 0) {
    return <Card><EmptyState icon={CalendarCheck} title={t('att.myTitle')} description={t('common.noData')} testId="att-history-empty" /></Card>;
  }

  return (
    <Card data-testid="att-history">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-4 border-b border-border">
        <SummaryChip label={t('att.present')} value={data.counts.present} color="text-emerald-600" />
        <SummaryChip label={t('att.late')} value={data.counts.late} color="text-amber-600" />
        <SummaryChip label={t('att.excused')} value={data.counts.excused} color="text-sky-600" />
        <SummaryChip label={t('att.absent')} value={data.counts.absent} color="text-rose-600" />
        <SummaryChip label={t('att.rate')} value={`${data.attendance_rate}%`} color="text-primary" testId="att-history-rate" />
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-3">{t('att.date')}</th>
              <th className="px-4 py-3">{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {data.records.map((r) => (
              <tr key={`${r.date}-${r.student_id}`} className="border-b border-border last:border-0" data-testid={`att-history-row-${r.date}`}>
                <td className="px-4 py-2 tabular-nums">{r.date}</td>
                <td className="px-4 py-2">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLE[r.status] || ''}`}>
                    {t(`att.${r.status}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const SummaryChip = ({ label, value, color, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    <p className={`text-xl font-semibold tabular-nums mt-0.5 ${color}`}>{value}</p>
  </div>
);
